import { installHostHorizontalSwipeGuard } from '../utils/hostHorizontalSwipeGuard';
import { isExplicitlySignedOut, readAccountUser, activeSavedAccount } from './accountSessions';
import { hasActiveWebSession } from './webSessionAuth';
import {
  legacyHostLinkMethod,
  legacyHostName,
  legacySnakeKey,
} from '../utils/legacyHostCompat';
import { HOST_CHROME_BODY_CLASS, HOST_OVERLAY_BODY_CLASS } from '../theme/hostBackSafeHeader';

// Field asset emoji configuration
// Extended to support all facility types with matching icons
export const FACILITY_EMOJI: Record<string, { standard: string; customId?: string; name: string }> = {
  // Main types
  well: { standard: '⛽', customId: '5368324170671202286', name: 'Well' },
  wells: { standard: '⛽', customId: '5368324170671202286', name: 'Wells' },
  swd: { standard: '🌊', customId: '5377305498966401693', name: 'SWD' },
  swds: { standard: '🌊', customId: '5377305498966401693', name: 'SWDs' },
  rig: { standard: '🏗️', customId: '5370869711888194012', name: 'Rig' },
  rigs: { standard: '🏗️', customId: '5370869711888194012', name: 'Rigs' },
  
  // Tank & Storage
  tb: { standard: '⚡', name: 'Tank Battery' },
  tankbattery: { standard: '⚡', name: 'Tank Battery' },
  gs: { standard: '🛢️', name: 'Gas Storage' },
  gasstorage: { standard: '🛢️', name: 'Gas Storage' },
  bgt: { standard: '🕳️', name: 'Below Grade Tank' },
  belowgradetank: { standard: '🕳️', name: 'Below Grade Tank' },
  
  // Flare & Emissions
  fs: { standard: '🔥', name: 'Flare Stack' },
  flarestack: { standard: '🔥', name: 'Flare Stack' },
  
  // Compressor & Pumps
  cs: { standard: '⚙️', name: 'Compressor' },
  compressor: { standard: '⚙️', name: 'Compressor' },
  cp: { standard: '💨', name: 'Crude Pump' },
  crudepump: { standard: '💨', name: 'Crude Pump' },
  
  // Plants & Processing
  gp: { standard: '🏭', name: 'Gas Plant' },
  gasplant: { standard: '🏭', name: 'Gas Plant' },
  rf: { standard: '🛢️', name: 'Refinery' },
  refinery: { standard: '🛢️', name: 'Refinery' },
  tp: { standard: '🧪', name: 'Treating Plant' },
  treatingplant: { standard: '🧪', name: 'Treating Plant' },
  inj: { standard: '💉', name: 'Injection Plant' },
  injectionplant: { standard: '💉', name: 'Injection Plant' },
  
  // Pipelines
  plg: { standard: '🔵', name: 'Gas Pipeline' },
  gaspipeline: { standard: '🔵', name: 'Gas Pipeline' },
  plc: { standard: '⚫', name: 'Crude Pipeline' },
  crudepipeline: { standard: '⚫', name: 'Crude Pipeline' },
  plw: { standard: '🔷', name: 'Water Pipeline' },
  waterpipeline: { standard: '🔷', name: 'Water Pipeline' },
  
  // Gathering & Metering
  ggs: { standard: '🔗', name: 'Gas Gathering' },
  gasgathering: { standard: '🔗', name: 'Gas Gathering' },
  mgs: { standard: '📊', name: 'Metering Station' },
  meteringstation: { standard: '📊', name: 'Metering Station' },
  wh: { standard: '🎯', name: 'Well Head' },
  wellhead: { standard: '🎯', name: 'Well Head' },
  wellpad: { standard: '🎯', name: 'Well Pad' },
  
  // Brine Well
  bw: { standard: '💧', name: 'Brine Well' },
  brinewell: { standard: '💧', name: 'Brine Well' },
  
  // Waste & Environmental
  pit: { standard: '🕳️', name: 'Pits' },
  pits: { standard: '🕳️', name: 'Pits' },
  wm: { standard: '🗑️', name: 'Waste Mgmt' },
  wastemgmt: { standard: '🗑️', name: 'Waste Management' },
  rfl: { standard: '♻️', name: 'Recycling' },
  recycling: { standard: '♻️', name: 'Recycling' },
  rm: { standard: '🩹', name: 'Remediate' },
  remediate: { standard: '🩹', name: 'Remediation' },
  ab: { standard: '🧹', name: 'Abatement' },
  abatement: { standard: '🧹', name: 'Abatement' },
  
  // Service & Regulatory
  sc: { standard: '🔧', name: 'Service Co.' },
  serviceco: { standard: '🔧', name: 'Service Company' },
  ci: { standard: '✅', name: 'Class I/II' },
  c2: { standard: '✅', name: 'Class I/II' },
  classiii: { standard: '✅', name: 'Class I/II' },
  
  // Generic facility
  facility: { standard: '🏢', name: 'Facility' },
  default: { standard: '📍', name: 'Location' }
};

export type FacilityType = string;

const HOST_WEB_APP_GLOBAL = legacyHostName();
const LEGACY_SIGNED_USER_STORAGE_KEY = legacySnakeKey('user_id');
const HOST_LINK_METHOD = legacyHostLinkMethod();
/** Telegram iOS title bar when the Mini App WebView overlaps it (never use 0). */
export const TELEGRAM_MINI_APP_HEADER_FALLBACK_PX = 48;

type HostWebAppWindow = Window & Record<string, { WebApp?: any } | undefined>;

export interface HostWebAppAuthDiagnostics {
  hasHostGlobal: boolean;
  hasWebApp: boolean;
  initDataLength: number;
  hasInitDataUnsafe: boolean;
  hasUnsafeUser: boolean;
  unsafeUserIdMasked: string | null;
  storedUserIdMasked: string | null;
  hasLaunchParamData: boolean;
  launchParamDataLength: number;
  platform: string | null;
  version: string | null;
  isMock: boolean;
}

class HostWebAppService {
  private static instance: HostWebAppService;
  public webApp: any;
  private activeBackButtonHandler: (() => void) | null = null;
  
  private constructor() {
    this.webApp = this.getNativeWebApp();
    // Only fall back to mock when the WebApp is completely absent.
    // If it exists but some methods are broken, keep the real object so that
    // ready() can still signal the host shell to dismiss its native loading indicator.
    if (!this.webApp) {
      console.warn('Host WebApp SDK not available; using mock');
      this.webApp = this.createMockWebApp();
    }
  }

  private getNativeWebApp(): any | null {
    return (window as unknown as HostWebAppWindow)[HOST_WEB_APP_GLOBAL]?.WebApp || null;
  }

  private refreshWebApp(): any | null {
    const realWebApp = this.getNativeWebApp();
    if (realWebApp && realWebApp !== this.webApp) {
      this.webApp = realWebApp;
    }
    return realWebApp || null;
  }

  private isCapacitorIosShell(): boolean {
    const capacitor = (window as any).Capacitor;
    let platform = '';
    try {
      platform = String(capacitor?.getPlatform?.() || capacitor?.platform || '').toLowerCase();
    } catch {
      platform = String(capacitor?.platform || '').toLowerCase();
    }
    return platform === 'ios' || (window.location.protocol === 'capacitor:' && window.location.hostname === 'localhost');
  }

  private getHostLaunchParams(): URLSearchParams {
    const params = new URLSearchParams();
    const appendParams = (raw: string) => {
      const value = raw.replace(/^[?#]/, '');
      if (!value) return;
      try {
        new URLSearchParams(value).forEach((paramValue, key) => {
          if (!params.has(key)) params.set(key, paramValue);
        });
      } catch {}
    };
    appendParams(window.location.hash);
    appendParams(window.location.search);
    return params;
  }

  private getLaunchInitData(): string {
    return this.getHostLaunchParams().get('tgWebAppData') || '';
  }

  private parseUserFromInitData(initData: string): any | null {
    if (!initData) return null;
    try {
      const rawUser = new URLSearchParams(initData).get('user');
      return rawUser ? JSON.parse(rawUser) : null;
    } catch {
      return null;
    }
  }

  private maskSignedUserId(id: string | null | undefined): string | null {
    if (!id) return null;
    if (id.length <= 4) return '••••';
    return `${id.slice(0, 3)}…${id.slice(-4)}`;
  }

  public static getInstance(): HostWebAppService {
    if (!HostWebAppService.instance) {
      HostWebAppService.instance = new HostWebAppService();
    }
    return HostWebAppService.instance;
  }

  public initialize() {
    // Re-detect the host bridge in case the async script loaded after construction.
    this.refreshWebApp();
    try { this.webApp.ready?.(); } catch (e) { console.warn('[HostWebApp] ready() failed:', e); }
    try { this.webApp.expand?.(); } catch (e) { console.warn('[HostWebApp] expand() failed:', e); }
    this.applyTheme();
    try {
      if (typeof this.webApp.disableVerticalSwipes === 'function') {
        this.webApp.disableVerticalSwipes();
        console.log('[HostWebApp] Disabled vertical swipes');
      }
    } catch (e) { console.warn('[HostWebApp] disableVerticalSwipes() failed:', e); }
    this.blockHorizontalSwipes();
    this.syncHostChromeClass();
    try {
      this.webApp.onEvent?.('themeChanged', () => { this.applyTheme(); });
    } catch (e) { console.warn('[HostWebApp] onEvent() failed:', e); }
    // Re-apply safe area after viewport settles post-expand (insets may be 0 until then)
    try {
      this.webApp.onEvent?.('viewportChanged', () => { this.applyTheme(); });
    } catch (e) { console.warn('[HostWebApp] viewportChanged listener failed:', e); }
    try {
      this.webApp.onEvent?.('safeAreaChanged', () => { this.applyTheme(); });
    } catch (e) { console.warn('[HostWebApp] safeAreaChanged listener failed:', e); }
    try {
      this.webApp.onEvent?.('contentSafeAreaChanged', () => { this.applyTheme(); });
    } catch (e) { console.warn('[HostWebApp] contentSafeAreaChanged listener failed:', e); }
    try {
      this.webApp.onEvent?.('fullscreenChanged', () => { this.applyTheme(); });
    } catch (e) { console.warn('[HostWebApp] fullscreenChanged listener failed:', e); }
  }

  /**
   * Intercepts horizontal touch gestures at the document level to prevent
   * host shells from interpreting them as back/minimize/close actions.
   */
  private blockHorizontalSwipes() {
    installHostHorizontalSwipeGuard();
  }

  private applyTheme() {
    const themeParams = this.webApp.themeParams;
    
    if (themeParams.bg_color) {
      document.documentElement.style.setProperty('--tg-theme-bg-color', themeParams.bg_color);
    }
    if (themeParams.text_color) {
      document.documentElement.style.setProperty('--tg-theme-text-color', themeParams.text_color);
    }
    if (themeParams.hint_color) {
      document.documentElement.style.setProperty('--tg-theme-hint-color', themeParams.hint_color);
    }
    if (themeParams.link_color) {
      document.documentElement.style.setProperty('--tg-theme-link-color', themeParams.link_color);
    }
    if (themeParams.button_color) {
      document.documentElement.style.setProperty('--tg-theme-button-color', themeParams.button_color);
    }
    if (themeParams.button_text_color) {
      document.documentElement.style.setProperty('--tg-theme-button-text-color', themeParams.button_text_color);
    }
    if (themeParams.secondary_bg_color) {
      document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', themeParams.secondary_bg_color);
    }

    // Apply viewport height
    const vh = this.webApp.viewportHeight;
    document.documentElement.style.setProperty('--tg-viewport-height', `${vh}px`);
    const vhStable = this.webApp.viewportStableHeight;
    document.documentElement.style.setProperty('--tg-viewport-stable-height', `${vhStable}px`);

    // Apply safe-area insets so dialogs/toolbars clear the host header.
    //
    // expand() (bot in-Telegram): WebView is already below the title bar.
    // Home-screen / fullscreen overlay: WebView covers the screen and Telegram
    // paints its header on top — inset must SUM notch + title bar, not max().
    const contentInsetTop = this.webApp.contentSafeAreaInset?.top || 0;
    const safeInsetTop = this.webApp.safeAreaInset?.top || 0;
    const platform = this.webApp.platform || '';
    const overlayCssFloor = `calc(env(safe-area-inset-top, 0px) + ${TELEGRAM_MINI_APP_HEADER_FALLBACK_PX}px)`;

    if (this.isCapacitorIosShell()) {
      document.body.classList.remove(HOST_OVERLAY_BODY_CLASS);
      document.documentElement.style.setProperty('--tg-content-safe-top', 'env(safe-area-inset-top, 0px)');
      return;
    }
    if (!this.isTelegramMiniApp()) {
      document.body.classList.remove(HOST_OVERLAY_BODY_CLASS);
      document.documentElement.style.setProperty(
        '--tg-content-safe-top',
        this.isTelegramClient()
          ? `${TELEGRAM_MINI_APP_HEADER_FALLBACK_PX}px`
          : 'env(safe-area-inset-top, 0px)',
      );
      return;
    }

    const vsh = this.webApp.viewportStableHeight || 0;
    const measuredHeaderHeight = vsh > 0 ? Math.max(0, Math.round(window.innerHeight - vsh)) : 0;
    const screenHeight = typeof window.screen?.height === 'number' ? window.screen.height : 0;
    const coversScreen = screenHeight > 0 && window.innerHeight >= screenHeight * 0.94;
    const overlaying = Boolean(this.webApp.isFullscreen)
      || measuredHeaderHeight > 8
      || (coversScreen && contentInsetTop === 0 && safeInsetTop === 0);

    document.body.classList.toggle(HOST_OVERLAY_BODY_CLASS, overlaying);

    if (overlaying) {
      const summed = contentInsetTop + safeInsetTop;
      document.documentElement.style.setProperty(
        '--tg-content-safe-top',
        summed >= TELEGRAM_MINI_APP_HEADER_FALLBACK_PX ? `${summed}px` : overlayCssFloor,
      );
      return;
    }

    let totalOffset: number;
    if (contentInsetTop > 0) {
      totalOffset = contentInsetTop + safeInsetTop;
    } else if (platform.startsWith('android')) {
      totalOffset = 56;
    } else {
      totalOffset = safeInsetTop + TELEGRAM_MINI_APP_HEADER_FALLBACK_PX;
    }
    document.documentElement.style.setProperty('--tg-content-safe-top', `${totalOffset}px`);
  }

  public getUserId(): string | null {
    if (isExplicitlySignedOut()) return null;
    if ((hasActiveWebSession() || activeSavedAccount()) && readAccountUser()?.id) return readAccountUser()!.id;
    this.refreshWebApp();
    const initDataUser = this.parseUserFromInitData(this.getInitData());
    const userId = this.webApp.initDataUnsafe?.user?.id?.toString()
      || initDataUser?.id?.toString()
      || null;
    
    // Debug logging
    console.log('🔐 Signed session debug:', {
      hasWebApp: !!this.webApp,
      hasInitData: !!this.webApp.initData,
      initDataLength: this.webApp.initData?.length || 0,
      hasInitDataUnsafe: !!this.webApp.initDataUnsafe,
      hasUser: !!this.webApp.initDataUnsafe?.user,
      userId: userId,
      platform: this.webApp.platform,
      version: this.webApp.version
    });
    
    // If no user ID and we're in a dev/testing environment, use a test ID
    // This allows testing facility submissions outside the signed host shell
    if (!userId) {
      const isLocalhost = window.location.hostname === 'localhost';
      const isPagesPreview = window.location.hostname.includes('pages.dev');
      const isTestMode = window.location.search.includes('testMode=true');
      const hasNoSignedContext = !this.webApp.initData || this.webApp.initData.length === 0;
      
      if ((isLocalhost || isPagesPreview || isTestMode) && hasNoSignedContext) {
        // Check for existing dev user ID
        const storedDevId = localStorage.getItem('dev-user-id');
        if (storedDevId) {
          console.log('📦 Using stored dev user ID:', storedDevId);
          return storedDevId;
        }
        
        // Generate new dev user ID
        const devUserId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? `dev-${crypto.randomUUID()}`
          : `dev-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('dev-user-id', devUserId);
        console.warn('⚠️ Generated new dev user ID:', devUserId);
        return devUserId;
      }
      
      // For production without signed host context, check if user has previously authenticated
      // by looking for a stored session
      const storedUserId = localStorage.getItem(LEGACY_SIGNED_USER_STORAGE_KEY);
      if (storedUserId) {
        console.log('📦 Using stored user ID:', storedUserId);
        return storedUserId;
      }
    } else {
      // Store the user ID for offline use
      localStorage.setItem(LEGACY_SIGNED_USER_STORAGE_KEY, userId);
    }
    
    return userId;
  }

  public getUser() {
    if (isExplicitlySignedOut()) return null;
    if ((hasActiveWebSession() || activeSavedAccount()) && readAccountUser()) return readAccountUser();
    // Re-detect the host bridge in case the async script loaded after construction.
    this.refreshWebApp();
    const user = this.webApp.initDataUnsafe?.user || this.parseUserFromInitData(this.getInitData());
    // Only return user if the runtime bridge supplied valid user data.
    // No mock user; require a signed host user.
    if (!user || !user.id) {
      return null;
    }
    return user;
  }

  public getUserName(): string | null {
    const user = this.getUser();
    if (!user) return null;
    if (user.first_name) {
      return user.last_name 
        ? `${user.first_name} ${user.last_name}`
        : user.first_name;
    }
    return user.username || null;
  }

  public showPopup(params: {
    title?: string;
    message: string;
    buttons?: Array<{ id: string; type?: string; text: string }>;
  }, callback?: (buttonId: string) => void) {
    // Check version support - showPopup requires version 6.2+
    const version = this.webApp.version || '6.0';
    const [major, minor] = version.split('.').map(Number);
    const supportsPopup = major > 6 || (major === 6 && minor >= 2);
    
    if (supportsPopup && this.webApp.showPopup) {
      try {
        this.webApp.showPopup(params, callback);
      } catch (e) {
        console.warn('showPopup failed, using fallback:', e);
        this.showPopupFallback(params, callback);
      }
    } else {
      this.showPopupFallback(params, callback);
    }
  }
  
  private showPopupFallback(params: {
    title?: string;
    message: string;
    buttons?: Array<{ id: string; type?: string; text: string }>;
  }, callback?: (buttonId: string) => void) {
    // Fallback for dev/non-host environments or older versions
    let message = params.title ? `${params.title}\n\n` : '';
    message += params.message;
    
    // Check if there's specifically an upgrade button
    const hasUpgradeButton = params.buttons?.some(b => b.id === 'upgrade');
    
    if (hasUpgradeButton && params.buttons && params.buttons.length > 1) {
      // Use confirm for upgrade prompts
      const confirmed = confirm(message + '\n\nClick OK to upgrade or Cancel to dismiss.');
      if (callback) {
        const buttonId = confirmed 
          ? (params.buttons.find(b => b.type !== 'cancel')?.id || 'ok')
          : 'cancel';
        callback(buttonId);
      }
    } else if (params.buttons && params.buttons.length > 1) {
      // Multiple choice - show button labels in message
      const buttonLabels = params.buttons.map((b, i) => `${i + 1}. ${b.text}`).join('\n');
      const choice = prompt(message + '\n\n' + buttonLabels + '\n\nEnter your choice (1-' + params.buttons.length + '):');
      if (callback) {
        const choiceNum = parseInt(choice || '0');
        if (choiceNum > 0 && choiceNum <= params.buttons.length) {
          callback(params.buttons[choiceNum - 1].id);
        }
      }
    } else {
      alert(message);
      if (callback && params.buttons && params.buttons.length > 0) {
        const defaultButton = params.buttons.find(b => b.type !== 'cancel');
        if (defaultButton) {
          callback(defaultButton.id);
        }
      }
    }
  }

  public getStartParam(): string | null {
    this.refreshWebApp();
    // Check the host bridge first, then fall back to URL query params.
    const sdkParam = this.webApp.initDataUnsafe?.start_param;
    if (sdkParam) return sdkParam;
    try {
      const params = this.getHostLaunchParams();
      const urlParam = params.get('tgWebAppStartParam') || params.get('startapp');
      if (urlParam) return urlParam;
    } catch {}
    return null;
  }

  /**
   * Get the raw initData string for authentication headers
   * Returns empty string for dev/test environments
   */
  public getInitData(): string {
    if (isExplicitlySignedOut() || hasActiveWebSession() || activeSavedAccount()) return '';
    // Re-detect the host bridge in case this service was constructed before injection.
    // Admin-only API surfaces depend on signed initData.
    this.refreshWebApp();
    const initData = this.webApp.initData || this.getLaunchInitData();
    
    // For dev/test environments without signed host context, return empty string
    // The middleware will handle this appropriately
    if (!initData) {
      const isLocalhost = window.location.hostname === 'localhost';
      const isTestMode = window.location.search.includes('testMode=true');
      if (isLocalhost || isTestMode) {
        console.warn('⚠️ No initData available (dev/test mode)');
      }
    }
    
    return initData;
  }

  public getAuthDiagnostics(): HostWebAppAuthDiagnostics {
    const realWebApp = this.refreshWebApp();
    const initData = this.getInitData();
    const launchInitData = this.getLaunchInitData();
    const unsafeUser = this.webApp.initDataUnsafe?.user || this.parseUserFromInitData(initData);
    const storedUserId = localStorage.getItem(LEGACY_SIGNED_USER_STORAGE_KEY);
    const launchParams = this.getHostLaunchParams();
    return {
      hasHostGlobal: Boolean((window as unknown as HostWebAppWindow)[HOST_WEB_APP_GLOBAL]),
      hasWebApp: Boolean(realWebApp),
      initDataLength: initData.length,
      hasInitDataUnsafe: Boolean(this.webApp.initDataUnsafe),
      hasUnsafeUser: Boolean(unsafeUser?.id),
      unsafeUserIdMasked: this.maskSignedUserId(unsafeUser?.id?.toString() || null),
      storedUserIdMasked: this.maskSignedUserId(storedUserId),
      hasLaunchParamData: Boolean(launchInitData),
      launchParamDataLength: launchInitData.length,
      platform: this.webApp.platform || launchParams.get('tgWebAppPlatform') || null,
      version: this.webApp.version || launchParams.get('tgWebAppVersion') || null,
      isMock: !realWebApp,
    };
  }

  public showMainButton(text: string, onClick: () => void) {
    this.webApp.MainButton.setText(text);
    this.webApp.MainButton.show();
    this.webApp.MainButton.onClick(onClick);
  }

  public hideMainButton() {
    this.webApp.MainButton.hide();
  }

  public syncHostChromeClass() {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle(HOST_CHROME_BODY_CLASS, this.isTelegramClient());
    if (!this.isTelegramClient()) {
      document.body.classList.remove(HOST_OVERLAY_BODY_CLASS);
    }
  }

  public isTelegramMiniApp(): boolean {
    if (this.isCapacitorIosShell()) return false;
    const realWebApp = this.getNativeWebApp();
    if (!realWebApp) return false;
    const platform = String(realWebApp.platform || '').toLowerCase();
    const hasInitData = typeof realWebApp.initData === 'string' && realWebApp.initData.length > 0;
    return hasInitData || (platform.length > 0 && platform !== 'web');
  }

  /** Mini App bridge or Telegram in-app browser — same Voice Nav chrome. */
  public isTelegramClient(): boolean {
    if (this.isCapacitorIosShell()) return false;
    if (this.isTelegramMiniApp()) return true;
    const ua = typeof navigator !== 'undefined' ? String(navigator.userAgent || '') : '';
    if (/Telegram/i.test(ua)) return true;
    try {
      if (typeof window !== 'undefined' && (window as { Telegram?: { WebApp?: unknown } }).Telegram?.WebApp) {
        return true;
      }
    } catch {
      /* no window */
    }
    return false;
  }

  public hasNativeBackButton(): boolean {
    if (this.isCapacitorIosShell()) return false;

    const realWebApp = this.getNativeWebApp();
    if (!realWebApp?.BackButton) return false;

    const platform = String(realWebApp.platform || '').toLowerCase();
    const hasInitData = typeof realWebApp.initData === 'string' && realWebApp.initData.length > 0;
    const looksLikeHostBridge = hasInitData || (platform.length > 0 && platform !== 'web');

    return Boolean(
      looksLikeHostBridge
      && typeof realWebApp.BackButton.show === 'function'
      && typeof realWebApp.BackButton.onClick === 'function'
    );
  }

  public showBackButton(onClick: () => void) {
    if (this.activeBackButtonHandler) {
      try {
        this.webApp.BackButton.offClick?.(this.activeBackButtonHandler);
      } catch {
        // Older clients may not support offClick; showing the new handler still works.
      }
    }

    this.activeBackButtonHandler = onClick;
    this.webApp.BackButton.show();
    this.webApp.BackButton.onClick(onClick);
  }

  public hideBackButton() {
    if (this.activeBackButtonHandler) {
      try {
        this.webApp.BackButton.offClick?.(this.activeBackButtonHandler);
      } catch {
        // Ignore cleanup failures from older host clients.
      }
      this.activeBackButtonHandler = null;
    }

    this.webApp.BackButton.hide();
  }

  /** Enter host fullscreen mode when available.
   * This also disables the swipe-back gesture so users won't accidentally
   * close the app while interacting with sliders or AR controls.
   */
  public enterFullscreen(): boolean {
    try {
      const wa = this.webApp as any;
      if (typeof wa.requestFullscreen === 'function') {
        wa.requestFullscreen();
        return true;
      }
    } catch (e) { console.warn('[HostWebApp] requestFullscreen() failed:', e); }
    return false;
  }

  /** Exit host fullscreen mode and re-enable swipe-back. */
  public exitFullscreen(): boolean {
    try {
      const wa = this.webApp as any;
      if (typeof wa.exitFullscreen === 'function') {
        wa.exitFullscreen();
        return true;
      }
    } catch (e) { console.warn('[HostWebApp] exitFullscreen() failed:', e); }
    return false;
  }

  public hapticFeedback(type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning') {
    // HapticFeedback requires host WebApp support.
    // Silently skip on older versions to avoid console warnings
    const version = parseFloat(this.webApp.version || '0');
    if (version < 6.1 || !this.webApp.HapticFeedback) {
      return;
    }
    
    try {
      if (type === 'success' || type === 'error' || type === 'warning') {
        this.webApp.HapticFeedback.notificationOccurred(type);
      } else {
        this.webApp.HapticFeedback.impactOccurred(type as 'light' | 'medium' | 'heavy');
      }
    } catch (e) {
      // Silently ignore if haptic feedback fails
    }
  }

  /**
   * Check if the signed host user has premium bridge capabilities.
   */
  public isPremium(): boolean {
    return this.webApp.initDataUnsafe?.user?.is_premium === true;
  }

  /**
   * Get facility emoji icon from bridge custom emoji support or standard fallback.
   * Returns a React-compatible element (span with emoji or custom emoji element)
   * Supports all facility types including codes like TB, FS, CS, etc.
   */
  public getFacilityEmoji(facilityType: FacilityType): { type: 'standard' | 'custom', content: string, customId?: string } {
    // Normalize the type: lowercase and remove spaces/dashes
    const typeKey = facilityType?.toLowerCase().replace(/[\s_-]/g, '') || 'default';
    const emoji = FACILITY_EMOJI[typeKey] || FACILITY_EMOJI.default;
    
    if (this.isPremium() && emoji.customId) {
      return {
        type: 'custom',
        content: emoji.standard,
        customId: emoji.customId
      };
    } else {
      return {
        type: 'standard',
        content: emoji.standard
      };
    }
  }

  public openLink(url: string) {
    try {
      if (typeof this.webApp?.openLink === 'function') {
        this.webApp.openLink(url, { try_instant_view: false });
        return true;
      }
    } catch (error) {
      console.warn('[HostWebApp] openLink failed, using browser fallback:', error);
    }
    try {
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (opened) {
        try { opened.opener = null; } catch {}
        return true;
      }
    } catch (error) {
      console.warn('[HostWebApp] browser open fallback failed:', error);
    }
    return false;
  }

  public openHostLink(url: string) {
    // Opens an external link through the runtime bridge when available.
    const openBridgeLink = this.webApp[HOST_LINK_METHOD];
    if (typeof openBridgeLink === 'function') {
      openBridgeLink.call(this.webApp, url);
    } else {
      // Fallback for development
      window.open(url, '_blank');
    }
  }

  /**
   * Telegram: Mini App share (switchInlineQuery / t.me/share).
   * Native/browser: Web Share API, else clipboard.
   */
  public shareApp(text?: string) {
    const shareText = text || '🛢️ Check out ZiaWellNav - the best oilfield navigation app for New Mexico!';
    const url = 'https://ziawellnav.com';

    if (this.isTelegramClient()) {
      try {
        if (typeof this.webApp?.switchInlineQuery === 'function') {
          this.webApp.switchInlineQuery(shareText, ['users', 'groups', 'channels']);
          return;
        }
      } catch {
        /* fall through to t.me/share */
      }
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`;
      try {
        if (typeof this.webApp?.openTelegramLink === 'function') {
          this.webApp.openTelegramLink(shareUrl);
          return;
        }
      } catch {
        /* fall through */
      }
      this.openHostLink(shareUrl);
      return;
    }

    if (navigator.share) {
      void navigator.share({ title: 'ZiaWellNav', text: shareText, url }).catch(() => undefined);
      return;
    }
    void navigator.clipboard?.writeText(`${shareText}\n${url}`);
  }

  /**
   * Share a specific location/facility with friends
   */
  public shareLocation(name: string, lat: number, lng: number, type?: string) {
    const emoji = type ? (FACILITY_EMOJI[type.toLowerCase()]?.standard || '📍') : '📍';
    const shareText = `${emoji} ${name}\n📍 ${lat.toFixed(6)}, ${lng.toFixed(6)}\n\nFound on ZiaWellNav!`;
    this.shareApp(shareText);
  }

  public close() {
    this.webApp.close();
  }

  public isAndroid(): boolean {
    return this.webApp.platform === 'android' || 
           /android/i.test(navigator.userAgent);
  }

  public showAlert(message: string): void {
    try {
      if (typeof this.webApp.showAlert === 'function') {
        this.webApp.showAlert(message);
      } else {
        alert(message);
      }
    } catch (err) {
      console.warn('Host showAlert unsupported, using window.alert', err);
      alert(message);
    }
  }

  /**
   * Copy text to clipboard with haptic feedback
   */
  public async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      this.hapticFeedback('success');
      return true;
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      // Fallback: try execCommand
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.hapticFeedback('success');
        return true;
      } catch (fallbackErr) {
        console.error('Fallback clipboard copy failed:', fallbackErr);
        this.hapticFeedback('error');
        return false;
      }
    }
  }

  public showConfirm(message: string, callback?: (confirmed: boolean) => void): void {
    try {
      if (typeof this.webApp.showConfirm === 'function') {
        this.webApp.showConfirm(message, callback);
      } else {
        const res = confirm(message);
        if (callback) callback(res);
      }
    } catch (err) {
      console.warn('Host showConfirm unsupported, using window.confirm', err);
      const res = confirm(message);
      if (callback) callback(res);
    }
  }

  // ===== HOST BRIDGE FEATURES =====

  /**
   * Voice-to-Text: Record voice message and get transcription
   * Uses host bridge transcription when available.
   */
  public async requestVoiceMessage(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.webApp.requestVoiceMessage) {
        reject(new Error('HOST_API_UNAVAILABLE'));
        return;
      }

      this.webApp.requestVoiceMessage((voiceData: { transcript?: string; file?: Blob }) => {
        if (voiceData.transcript) {
          resolve(voiceData.transcript);
        } else {
          reject(new Error('Voice transcription is unavailable. Use browser voice recognition or type your search.'));
        }
      });
    });
  }

  /**
   * Check if host voice message API is available
   */
  public isVoiceMessageSupported(): boolean {
    return typeof this.webApp.requestVoiceMessage === 'function';
  }

  /**
   * Request user's location (better than browser geolocation)
   * One-tap GPS access, no browser permission dialog
   */
  public async requestLocation(): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      if (!this.webApp.requestLocation) {
        console.log('[HostWebApp] requestLocation not available');
        reject(new Error('Location sharing not supported'));
        return;
      }

      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        console.log('[HostWebApp] requestLocation timed out');
        reject(new Error('Location request timed out'));
      }, 10000);

      console.log('[HostWebApp] Calling webApp.requestLocation...');
      this.webApp.requestLocation((location: { latitude: number; longitude: number } | null) => {
        clearTimeout(timeout);
        if (location) {
          console.log('[HostWebApp] Got location:', location);
          resolve(location);
        } else {
          console.log('[HostWebApp] Location denied/null');
          reject(new Error('Location permission denied'));
        }
      });
    });
  }

  /**
   * Request contact sharing (for operator contact)
   */
  public async requestContact(): Promise<{ phone: string; firstName: string; lastName?: string }> {
    return new Promise((resolve, reject) => {
      if (!this.webApp.requestContact) {
        reject(new Error('Contact sharing not supported'));
        return;
      }

      this.webApp.requestContact((result: any) => {
        console.log('[HostWebApp] requestContact result:', JSON.stringify(result));
        // Newer host bridges may return a boolean and keep contact data in the event.
        // Older bridges may return the contact object directly.
        // Handle both formats:
        const contact = result?.responseUnsafe?.contact  // v6.9+ nested format
          || result?.contact                              // alternative nested
          || (result?.phone_number ? result : null);      // legacy direct format
        if (contact?.phone_number) {
          resolve({
            phone: contact.phone_number,
            firstName: contact.first_name || '',
            lastName: contact.last_name
          });
        } else if (result === true || result?.status === 'sent') {
          // Boolean callback — contact was shared but data comes via event
          // Fall back to reading from the WebApp object
          reject(new Error('Contact shared but data not accessible — please try again'));
        } else {
          reject(new Error('Contact sharing denied'));
        }
      });
    });
  }

  /**
   * Request write access to send messages to user
   */
  public async requestWriteAccess(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.webApp.requestWriteAccess) {
        resolve(false);
        return;
      }

      this.webApp.requestWriteAccess((granted: boolean) => {
        resolve(granted);
      });
    });
  }

  /**
   * Check if CloudStorage is available
   * CloudStorage requires host WebApp storage support.
   */
  private isCloudStorageAvailable(): boolean {
    // Check if CloudStorage object exists
    if (!this.webApp?.CloudStorage) {
      return false;
    }
    
    // Check if methods are available
    if (typeof this.webApp.CloudStorage.setItem !== 'function') {
      return false;
    }
    
    // Check version (CloudStorage added in 6.9)
    const version = this.webApp.version || '6.0';
    const [major, minor] = version.split('.').map(Number);
    
    // CloudStorage is available from version 6.9 onwards
    if (major < 6 || (major === 6 && minor < 9)) {
      console.log(`CloudStorage not available in host WebApp version ${version} (requires 6.9+). Using localStorage fallback.`);
      return false;
    }
    
    return true;
  }

  /**
   * Save favorites to host CloudStorage when available.
   * Falls back to localStorage if CloudStorage is not available
   */
  public async saveFavorite(facilityId: string, facilityData: any): Promise<void> {
    const key = `favorite_${facilityId}`;
    const value = JSON.stringify(facilityData);
    
    // Use CloudStorage if available from the host bridge.
    if (this.isCloudStorageAvailable()) {
      try {
        return new Promise((resolve, reject) => {
          this.webApp.CloudStorage.setItem(key, value, (error: string | null) => {
            if (error) {
              console.warn('CloudStorage.setItem failed, falling back to localStorage:', error);
              // Fall back to localStorage on error
              try {
                localStorage.setItem(key, value);
                resolve();
              } catch (e) {
                reject(e);
              }
            } else {
              resolve();
            }
          });
        });
      } catch (error) {
        console.warn('CloudStorage operation failed, falling back to localStorage:', error);
        // If CloudStorage throws an error, fall back to localStorage
        localStorage.setItem(key, value);
        return Promise.resolve();
      }
    }
    
    // Fallback to localStorage
    try {
      localStorage.setItem(key, value);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async removeFavorite(facilityId: string): Promise<void> {
    const key = `favorite_${facilityId}`;
    
    // Use CloudStorage if available from the host bridge.
    if (this.isCloudStorageAvailable()) {
      try {
        return new Promise((resolve, reject) => {
          this.webApp.CloudStorage.removeItem(key, (error: string | null) => {
            if (error) {
              console.warn('CloudStorage.removeItem failed, falling back to localStorage:', error);
              // Fall back to localStorage on error
              try {
                localStorage.removeItem(key);
                resolve();
              } catch (e) {
                reject(e);
              }
            } else {
              resolve();
            }
          });
        });
      } catch (error) {
        console.warn('CloudStorage operation failed, falling back to localStorage:', error);
        // If CloudStorage throws an error, fall back to localStorage
        localStorage.removeItem(key);
        return Promise.resolve();
      }
    }
    
    // Fallback to localStorage
    try {
      localStorage.removeItem(key);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getFavorites(): Promise<any[]> {
    // Use CloudStorage if available from the host bridge.
    if (this.isCloudStorageAvailable()) {
      try {
        return new Promise((resolve, reject) => {
          this.webApp.CloudStorage.getKeys((error: string | null, keys?: string[]) => {
            if (error || !keys) {
              console.warn('CloudStorage.getKeys failed, falling back to localStorage:', error);
              // Fall back to localStorage on error
              this.getFavoritesFromLocalStorage().then(resolve).catch(reject);
              return;
            }

            const favoriteKeys = keys.filter(k => k.startsWith('favorite_'));
            
            if (favoriteKeys.length === 0) {
              resolve([]);
              return;
            }

            this.webApp.CloudStorage.getItems(favoriteKeys, (error: string | null, values?: Record<string, string>) => {
              if (error || !values) {
                console.warn('CloudStorage.getItems failed, falling back to localStorage:', error);
                // Fall back to localStorage on error
                this.getFavoritesFromLocalStorage().then(resolve).catch(reject);
                return;
              }

              const favorites = Object.values(values).map(v => {
                try {
                  return JSON.parse(v);
                } catch {
                  return null;
                }
              }).filter(Boolean);
              resolve(favorites);
            });
          });
        });
      } catch (error) {
        console.warn('CloudStorage operation failed, falling back to localStorage:', error);
        // If CloudStorage throws an error, fall back to localStorage
        return this.getFavoritesFromLocalStorage();
      }
    }
    
    // Fallback to localStorage
    return this.getFavoritesFromLocalStorage();
  }

  /**
   * Get favorites from localStorage (fallback method)
   */
  private async getFavoritesFromLocalStorage(): Promise<any[]> {
    try {
      const favorites = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('favorite_')) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              favorites.push(JSON.parse(value));
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }
      return Promise.resolve(favorites);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Save app settings to CloudStorage
   * Falls back to localStorage if CloudStorage is not available
   */
  public async saveSetting(key: string, value: any): Promise<void> {
    const stringValue = JSON.stringify(value);
    
    // Use CloudStorage if available from the host bridge.
    if (this.isCloudStorageAvailable()) {
      return new Promise((resolve, reject) => {
        this.webApp.CloudStorage.setItem(key, stringValue, (error: string | null) => {
          if (error) reject(new Error(error));
          else resolve();
        });
      });
    }
    
    // Fallback to localStorage
    try {
      localStorage.setItem(key, stringValue);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getSetting(key: string): Promise<any> {
    // Use CloudStorage if available from the host bridge.
    if (this.isCloudStorageAvailable()) {
      return new Promise((resolve, reject) => {
        this.webApp.CloudStorage.getItem(key, (error: string | null, value?: string) => {
          if (error) {
            reject(new Error(error));
          } else if (value) {
            try {
              resolve(JSON.parse(value));
            } catch {
              resolve(value);
            }
          } else {
            resolve(null);
          }
        });
      });
    }
    
    // Fallback to localStorage
    try {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          return Promise.resolve(JSON.parse(value));
        } catch {
          return Promise.resolve(value);
        }
      }
      return Promise.resolve(null);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Generic CloudStorage get - stores raw string values
   * Falls back to localStorage if CloudStorage is not available
   */
  public async cloudStorageGet(key: string): Promise<string | null> {
    // Use CloudStorage if available from the host bridge.
    if (this.isCloudStorageAvailable()) {
      return new Promise((resolve, reject) => {
        this.webApp.CloudStorage.getItem(key, (error: string | null, value?: string) => {
          if (error) {
            console.warn('CloudStorage.getItem failed, falling back to localStorage:', error);
            try {
              resolve(localStorage.getItem(key));
            } catch (e) {
              reject(e);
            }
          } else {
            resolve(value || null);
          }
        });
      });
    }
    
    // Fallback to localStorage
    try {
      return Promise.resolve(localStorage.getItem(key));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Generic CloudStorage set - stores raw string values
   * Falls back to localStorage if CloudStorage is not available
   */
  public async cloudStorageSet(key: string, value: string): Promise<void> {
    // Use CloudStorage if available from the host bridge.
    if (this.isCloudStorageAvailable()) {
      return new Promise((resolve, reject) => {
        this.webApp.CloudStorage.setItem(key, value, (error: string | null) => {
          if (error) {
            console.warn('CloudStorage.setItem failed, falling back to localStorage:', error);
            try {
              localStorage.setItem(key, value);
              resolve();
            } catch (e) {
              reject(e);
            }
          } else {
            resolve();
          }
        });
      });
    }
    
    // Fallback to localStorage
    try {
      localStorage.setItem(key, value);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Verify browser account login auth data.
  // Now just returns true - validation happens on the host side.
  public async verifyLoginAuth(_authData: any): Promise<boolean> {
    return true;
  }

  // Mock WebApp for development outside a host shell.
  private createMockWebApp(): any {
    const isLocalhost = window.location.hostname === 'localhost';
    const isPagesPreview = window.location.hostname.includes('pages.dev');
    const mockUser = (isLocalhost || isPagesPreview)
      ? { id: 123456789, first_name: 'Dev', last_name: 'User', username: 'devuser', language_code: 'en' }
      : null;
    return {
      initData: '',
      initDataUnsafe: {
        user: mockUser,
      },
      version: '7.0',
      platform: 'web',
      colorScheme: 'light',
      themeParams: {
        bg_color: '#ffffff',
        text_color: '#000000',
        hint_color: '#999999',
        link_color: '#168acd',
        button_color: '#40a7e3',
        button_text_color: '#ffffff',
        secondary_bg_color: '#f1f1f1',
      },
      isExpanded: true,
      viewportHeight: window.innerHeight,
      viewportStableHeight: window.innerHeight,
      headerColor: '#ffffff',
      backgroundColor: '#ffffff',
      isClosingConfirmationEnabled: false,
      BackButton: {
        isVisible: false,
        onClick: () => {},
        offClick: () => {},
        show: () => {},
        hide: () => {},
      },
      MainButton: {
        text: '',
        color: '#40a7e3',
        textColor: '#ffffff',
        isVisible: false,
        isActive: true,
        isProgressVisible: false,
        setText: () => {},
        onClick: () => {},
        offClick: () => {},
        show: () => {},
        hide: () => {},
        enable: () => {},
        disable: () => {},
        showProgress: () => {},
        hideProgress: () => {},
        setParams: () => {},
      },
      HapticFeedback: {
        impactOccurred: () => {},
        notificationOccurred: () => {},
        selectionChanged: () => {},
      },
      ready: () => {},
      expand: () => {},
      close: () => {},
      onEvent: () => {},
      offEvent: () => {},
      sendData: () => {},
      openLink: (url: string) => window.open(url, '_blank'),
      [HOST_LINK_METHOD]: () => {},
      openInvoice: () => {},
      showAlert: (message: string) => {
        alert(message);
      },
      showConfirm: (message: string, callback?: (confirmed: boolean) => void) => {
        const result = confirm(message);
        if (callback) callback(result);
      },
      showPopup: (params: any, callback?: (buttonId: string) => void) => {
        // Mock popup with alert
        let message = params.title ? `${params.title}\n\n` : '';
        message += params.message || '';
        alert(message);
        if (callback && params.buttons && params.buttons.length > 0) {
          // Return first non-cancel button
          const defaultButton = params.buttons.find((b: any) => b.type !== 'cancel');
          if (defaultButton) {
            callback(defaultButton.id);
          }
        }
      },
      requestVoiceMessage: (callback: (data: any) => void) => {
        console.warn('Mock: requestVoiceMessage not available in dev mode');
        callback({ transcript: 'navigate to blackjack federal 25' });
      },
      requestLocation: (callback: (location: any) => void) => {
        // Mock location (Hobbs, NM)
        callback({ latitude: 32.7026, longitude: -103.136 });
      },
      requestContact: (callback: (contact: any) => void) => {
        callback({ phone_number: '+15551234567', first_name: 'John', last_name: 'Doe' });
      },
      requestWriteAccess: (callback: (granted: boolean) => void) => {
        callback(true);
      },
      CloudStorage: {
        setItem: (key: string, value: string, callback?: (error: string | null) => void) => {
          localStorage.setItem(key, value);
          if (callback) callback(null);
        },
        getItem: (key: string, callback: (error: string | null, value?: string) => void) => {
          const value = localStorage.getItem(key);
          callback(null, value || undefined);
        },
        getItems: (keys: string[], callback: (error: string | null, values?: Record<string, string>) => void) => {
          const values: Record<string, string> = {};
          keys.forEach(key => {
            const value = localStorage.getItem(key);
            if (value) values[key] = value;
          });
          callback(null, values);
        },
        removeItem: (key: string, callback?: (error: string | null) => void) => {
          localStorage.removeItem(key);
          if (callback) callback(null);
        },
        getKeys: (callback: (error: string | null, keys?: string[]) => void) => {
          const keys = Object.keys(localStorage);
          callback(null, keys);
        }
      }
    };
  }
}

export const hostWebApp = HostWebAppService.getInstance();
